import React from 'react';
import styled from 'styled-components';
import { formatSaldo } from '../utils/formatters';
import { MONEDA_MOVIMIENTO, bucketDeMoneda } from '../constants/monedas';
import { formatSnapshotHora } from '../services/saldosService';
import AcreditacionesEsperadasSection from './saldos/AcreditacionesEsperadasSection';

const Overlay = styled.div`
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background-color: rgba(17, 24, 39, 0.8);
    display: flex; justify-content: center; align-items: center;
    z-index: 2050;
    @media (max-width: 600px) {
        padding: calc(env(safe-area-inset-top) + 0.5rem) 0.5rem calc(env(safe-area-inset-bottom) + 0.5rem);
    }
`;

const ModalContent = styled.div`
    background-color: ${props => props.theme.colors.surface};
    padding: 2rem;
    border-radius: 8px;
    width: 92%;
    max-width: 780px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    @media (max-width: 600px) {
        width: 100%;
        max-height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 1rem);
        padding: 1rem;
    }
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const TitleWrap = styled.div`
    h3 {
        font-family: ${props => props.theme.typography.titleFamily};
        font-size: 1.4rem;
        color: ${props => props.theme.colors.primary};
        margin: 0 0 0.25rem 0;
    }
    p {
        margin: 0;
        font-size: 0.85rem;
        color: ${props => props.theme.colors.textSecondary};
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1;
    &:hover { color: ${props => props.theme.colors.primary}; }
    @media (max-width: 600px) {
        min-width: 44px;
        min-height: 44px;
        margin: -0.5rem -0.5rem 0 0;
    }
`;

const Summary = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    @media (max-width: 600px) {
        grid-template-columns: 1fr;
        gap: 0.5rem;
        margin-bottom: 1rem;
    }
`;

const SummaryCard = styled.div`
    border-left: 4px solid ${props => props.color || props.theme.colors.primary};
    background-color: ${props => props.theme.colors.background};
    border-radius: ${props => props.theme.borderRadius};
    padding: 0.85rem 1rem;
    label {
        display: block;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        color: ${props => props.theme.colors.textSecondary};
        letter-spacing: 0.05em;
        margin-bottom: 0.2rem;
    }
    strong {
        font-size: 1.1rem;
        color: ${props => props.theme.colors.secondary};
    }
`;

const MovementsScroll = styled.div`
    overflow-y: auto;
    flex: 1;
    border: 1px solid ${props => props.theme.colors.lightGray};
    border-radius: ${props => props.theme.borderRadius};
`;

const SectionTitle = styled.div`
    background-color: ${props => props.$tone === 'pendiente' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(26, 40, 62, 0.05)'};
    padding: 0.5rem 0.9rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: ${props => props.theme.colors.secondary};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid ${props => props.theme.colors.lightGray};
`;

const MovementRow = styled.div`
    display: grid;
    grid-template-columns: 80px 1fr 110px 140px;
    gap: 0.75rem;
    padding: 0.6rem 0.9rem;
    border-bottom: 1px solid ${props => props.theme.colors.lightGray};
    font-size: 0.85rem;
    align-items: center;
    &:last-child { border-bottom: none; }
    /* Celular: tipo y monto arriba, descripción y estado debajo. */
    @media (max-width: 600px) {
        grid-template-columns: auto minmax(0, 1fr);
        row-gap: 0.3rem;
        & > :nth-child(2) { order: 2; grid-column: 1 / -1; }
        & > :nth-child(3) { order: 1; text-align: right; }
        & > :nth-child(4) { order: 3; grid-column: 1 / -1; }
    }
`;

const TipoBadge = styled.span`
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    background-color: ${props => {
        if (props.$tipo === 'caucion') return 'rgba(16, 185, 129, 0.15)';
        if (props.$tipo === 'pase') return 'rgba(234, 179, 8, 0.18)';
        if (props.$tipo === 'operacion') return 'rgba(59, 130, 246, 0.15)';
        if (props.$tipo === 'instruccion') return 'rgba(139, 92, 246, 0.15)';
        if (props.$tipo === 'acreditacion') return 'rgba(20, 184, 166, 0.15)';
        if (props.$tipo === 'licitacion') return 'rgba(244, 63, 94, 0.12)';
        return props.theme.colors.lightGray;
    }};
    color: ${props => {
        if (props.$tipo === 'caucion') return '#065F46';
        if (props.$tipo === 'pase') return '#854D0E';
        if (props.$tipo === 'operacion') return '#1E40AF';
        if (props.$tipo === 'instruccion') return '#5B21B6';
        if (props.$tipo === 'acreditacion') return '#115E59';
        if (props.$tipo === 'licitacion') return '#9F1239';
        return props.theme.colors.secondary;
    }};
`;

const MontoCell = styled.span`
    text-align: right;
    font-weight: 600;
    color: ${props => props.$positive ? '#065F46' : '#991B1B'};
    font-family: monospace;
`;

const EstadoCell = styled.span`
    font-size: 0.75rem;
    color: ${props => props.theme.colors.textSecondary};
    text-align: right;
`;

const EmptySection = styled.div`
    padding: 0.85rem 0.9rem;
    font-size: 0.8rem;
    color: ${props => props.theme.colors.textSecondary};
    font-style: italic;
`;

const SaldoDesgloseModal = ({
    isOpen, onClose, cuenta, moneda, snapshotInfo,
    acreditaciones = null, puedeMarcarAcreditaciones = false, onMarcarAcreditacion,
}) => {
    if (!isOpen || !cuenta) return null;

    const fmt = (value) => formatSaldo(value, moneda);
    const monedaLabel = MONEDA_MOVIMIENTO[bucketDeMoneda(moneda)];

    const renderMovementRow = (mov, idx) => (
        <MovementRow key={`${mov.tipo}-${mov.id || idx}`}>
            <TipoBadge $tipo={mov.tipo}>{mov.tipo}</TipoBadge>
            <span>{mov.descripcion}</span>
            <MontoCell $positive={mov.monto >= 0}>
                {mov.monto >= 0 ? '+' : ''}{fmt(mov.monto)}
            </MontoCell>
            <EstadoCell>{mov.estado}</EstadoCell>
        </MovementRow>
    );

    return (
        <Overlay onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <TitleWrap>
                        <h3>{cuenta.nombre}</h3>
                        <p>
                            Comitente {cuenta.nroComitente} — {monedaLabel}
                            {snapshotInfo && (
                                <> · Snapshot: <strong>{snapshotInfo.tipo}</strong> @ {formatSnapshotHora(snapshotInfo.uploadedAt)}</>
                            )}
                        </p>
                    </TitleWrap>
                    <CloseButton onClick={onClose} aria-label="Cerrar">×</CloseButton>
                </ModalHeader>

                <Summary>
                    <SummaryCard color="#6B7280">
                        <label>Saldo snapshot</label>
                        <strong>{fmt(cuenta.saldoSnapshot)}</strong>
                    </SummaryCard>
                    <SummaryCard color={cuenta.saldoReal >= 0 ? '#10B981' : '#EF4444'}>
                        <label>Saldo real</label>
                        <strong>{fmt(cuenta.saldoReal)}</strong>
                    </SummaryCard>
                    <SummaryCard color="#F59E0B">
                        <label>Saldo proyectado</label>
                        <strong>{fmt(cuenta.saldoProyectado)}</strong>
                    </SummaryCard>
                </Summary>

                <MovementsScroll>
                    <SectionTitle>Movimientos reales (Hechos) — {cuenta.movimientosReales.length}</SectionTitle>
                    {cuenta.movimientosReales.length === 0 ? (
                        <EmptySection>Sin movimientos Hechos posteriores al snapshot.</EmptySection>
                    ) : (
                        cuenta.movimientosReales.map(renderMovementRow)
                    )}

                    <SectionTitle $tone="pendiente">Pendientes (En Proceso / Pendiente) — {cuenta.movimientosPendientes.length}</SectionTitle>
                    {cuenta.movimientosPendientes.length === 0 ? (
                        <EmptySection>Sin movimientos pendientes.</EmptySection>
                    ) : (
                        cuenta.movimientosPendientes.map(renderMovementRow)
                    )}

                    {acreditaciones && (
                        <AcreditacionesEsperadasSection
                            resumen={acreditaciones}
                            moneda={moneda}
                            puedeMarcar={puedeMarcarAcreditaciones}
                            onMarcar={onMarcarAcreditacion}
                        />
                    )}
                </MovementsScroll>
            </ModalContent>
        </Overlay>
    );
};

export default SaldoDesgloseModal;
